import type { Container } from "pixi.js";
import type { PresentationTaskRunner } from "../../../runner/PresentationTaskRunner";
import type { LandBounceTuning } from "../LandBounceTuning";
import type { ColumnLanding, GridLandReaction } from "./GridLandReaction";

type Point = { x: number; y: number };

/**
 * Dips `target` down `tuning.pixels` and settles it back whenever a column lands containing one of
 * `triggerSymbolIds` (a scatter, a bonus symbol). A new bounce supersedes an in-flight one through a
 * generation token and restarts from the node's current spot; home is captured only while not
 * bouncing. Timing runs on the presentation runner outside any scope, so turbo, pause and frame-step
 * scale it and a slam, which finishes scopes, never cuts it short.
 */
export class LandBounce implements GridLandReaction {
  private readonly target: Container;
  private readonly runner: PresentationTaskRunner;
  private readonly triggerSymbolIds: ReadonlySet<string>;
  private readonly tuning: LandBounceTuning;
  private readonly home: Point = { x: 0, y: 0 };
  private active = false;
  private generation = 0;

  public constructor(input: { target: Container; runner: PresentationTaskRunner; triggerSymbolIds: ReadonlySet<string>; tuning: LandBounceTuning }) {
    this.target = input.target;
    this.runner = input.runner;
    this.triggerSymbolIds = input.triggerSymbolIds;
    this.tuning = input.tuning;
  }

  public onColumnLanded(landing: ColumnLanding): void {
    if (this.target.destroyed) return;
    if (!landing.symbolIds.some((id) => this.triggerSymbolIds.has(id))) return;
    void this.run();
  }

  private async run(): Promise<void> {
    const node = this.target;
    if (!this.active) {
      this.home.x = node.position.x;
      this.home.y = node.position.y;
    }
    this.active = true;
    const generation = ++this.generation;
    const home = this.home;
    const down: Point = { x: home.x, y: home.y + this.tuning.pixels }; // y-down: "down" is +y
    const start: Point = { x: node.position.x, y: node.position.y };
    const lerp = (a: Point, b: Point, k: number): void => {
      node.position.set(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k);
    };
    const live = (): boolean => generation === this.generation && !node.destroyed;

    await this.runner.tweenProgress(Math.max(0.001, this.tuning.downSeconds), (k) => { if (live()) lerp(start, down, k); }, undefined, { easing: this.tuning.easing });
    if (!live()) return;
    await this.runner.tweenProgress(Math.max(0.001, this.tuning.upSeconds), (k) => { if (live()) lerp(down, home, k); }, undefined, { easing: this.tuning.easing });
    if (!live()) return;
    node.position.set(home.x, home.y);
    this.active = false;
  }
}
